import type {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next/types';
import type { ReactElement } from 'react';
import type { NextPageWithLayout, PageComponentProps } from '../../../_app';
import type { DirectGiftI } from '../../../../src/features/donations/components/DirectGift';
import type { AbstractIntlMessages } from 'next-intl';
import type { Tenant } from '@planet-sdk/common';

import {
  constructPathsForTenantSlug,
  getTenantConfig,
} from '../../../../src/utils/multiTenancy/helpers';
import getMessagesForPage from '../../../../src/utils/language/getMessagesForPage';
import { useEffect, useState } from 'react';
import ProjectsLayout from '../../../../src/features/common/Layout/ProjectsLayout';
import MobileProjectsLayout from '../../../../src/features/common/Layout/ProjectsLayout/MobileProjectsLayout';
import ProjectsSection from '../../../../src/features/projectsV2/ProjectsSection';
import DirectGift from '../../../../src/features/donations/components/DirectGift';
import { useTenantStore } from '../../../../src/stores/tenantStore';
import { defaultTenant } from '../../../../tenant.config';

const ProjectListPage: NextPageWithLayout = ({ isMobile }) => {
  const isInitialized = useTenantStore((state) => state.isInitialized);

  const [directGift, setDirectGift] = useState<DirectGiftI | null>(null);

  useEffect(() => {
    const storedDirectGift = localStorage.getItem('directGift');
    if (!storedDirectGift) return;
    try {
      const parsedDirectGift = JSON.parse(storedDirectGift);
      if (
        parsedDirectGift &&
        typeof parsedDirectGift.id === 'string' &&
        typeof parsedDirectGift.displayName === 'string' &&
        typeof parsedDirectGift.type === 'string'
      ) {
        setDirectGift(parsedDirectGift);
      } else {
        localStorage.removeItem('directGift');
      }
    } catch {
      localStorage.removeItem('directGift');
    }
  }, []);

  if (!isInitialized) return <></>;

  return (
    <>
      <ProjectsSection isMobile={isMobile} />
      {directGift !== null && (
        <DirectGift directGift={directGift} setDirectGift={setDirectGift} />
      )}
    </>
  );
};

ProjectListPage.getLayout = function getLayout(
  page: ReactElement,
  pageComponentProps: PageComponentProps
): ReactElement {
  const layoutProps = {
    isMobile: pageComponentProps.isMobile,
  } as const;

  return pageComponentProps.isMobile ? (
    <MobileProjectsLayout {...layoutProps}>{page}</MobileProjectsLayout>
  ) : (
    <ProjectsLayout {...layoutProps}>{page}</ProjectsLayout>
  );
};

export default ProjectListPage;

export const getStaticPaths: GetStaticPaths = async () => {
  const subDomainPaths = await constructPathsForTenantSlug();

  const paths =
    subDomainPaths?.map((path) => {
      return {
        params: {
          slug: path.params.slug,
          locale: 'en',
        },
      };
    }) ?? [];

  return {
    paths,
    fallback: 'blocking',
  };
};

interface PageProps {
  messages: AbstractIntlMessages;
  tenantConfig: Tenant;
}

export const getStaticProps: GetStaticProps<PageProps> = async (
  context: GetStaticPropsContext
): Promise<GetStaticPropsResult<PageProps>> => {
  const tenantConfig =
    (await getTenantConfig(context.params?.slug as string)) ?? defaultTenant;

  const messages = await getMessagesForPage({
    locale: context.params?.locale as string,
    filenames: [
      'common',
      'maps',
      'country',
      'projectDetails',
      'donate',
      'allProjects',
      'project',
      'me',
    ],
  });

  return {
    props: {
      messages,
      tenantConfig,
    },
  };
};
